import { ModelType, SearchableRecordInstantSearch } from '@livv/models';
import { Box } from '@mui/material';
import { Hit } from 'instantsearch.js';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState } from 'react';
import SecondaryAttribute, { SecondaryAttributeData } from './SecondaryAttribute';
import ExpandMoreButton from '@components/ExpandMoreButton';
import { getHighlightedAttributes } from '@utils/search/highlight';

interface SecondaryAttributesProps {
    hit: Hit<SearchableRecordInstantSearch>;
    secondaryAttributes?: SecondaryAttributeData[];
}

const SecondaryAttributes: FC<SecondaryAttributesProps> = ({ hit, secondaryAttributes }) => {
    const { t } = useTranslation('common');
    const [showSecondaryAttributes, setShowSecondaryAttributes] = useState(false);
    const highlightedAttributes = getHighlightedAttributes(hit);
    const { modelType } = hit;

    const isValidAttribute = (attribute: string): boolean => {
        const value = hit[attribute];

        return (
            (Array.isArray(value) && value.filter(Boolean).length > 0) ||
            (typeof value === 'string' && value.trim().length > 0)
        );
    };
    const filteredAttributes = secondaryAttributes?.filter(({ attribute }) =>
        isValidAttribute(attribute),
    );

    if (!filteredAttributes?.length) {
        return null;
    }

    if (ModelType.DECISION === modelType) {
        return (
            <Box
                sx={{
                    border: '1px dashed',
                    borderColor: 'grey.500',
                    borderRadius: 0.5,
                    p: 1,
                    width: 'fit-content',
                }}
            >
                {filteredAttributes.length === 1 ? (
                    <SecondaryAttribute
                        attribute={filteredAttributes[0].attribute}
                        attributeType={filteredAttributes[0].attributeType}
                        hit={hit}
                        label={filteredAttributes[0].label}
                    />
                ) : (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {filteredAttributes.map(({ attribute, attributeType, label }) => (
                            <Box
                                key={`secondary-attribute-${label}`}
                                component="li"
                                sx={{ '&::marker': { fontSize: 12 } }}
                            >
                                <SecondaryAttribute
                                    attribute={attribute}
                                    attributeType={attributeType}
                                    hit={hit}
                                    label={label}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    const displayedSecondaryHighlightedAttributes = filteredAttributes.filter(({ attribute }) =>
        highlightedAttributes.includes(attribute),
    );

    const displayedSecondaryAttributes = filteredAttributes.filter(
        ({ attribute }) => !highlightedAttributes.includes(attribute),
    );

    return (
        <>
            {displayedSecondaryHighlightedAttributes.map(({ attribute, attributeType, label }) => (
                <SecondaryAttribute
                    key={`highlighted-secondary-attribute-${label}`}
                    attribute={attribute}
                    attributeType={attributeType}
                    hit={hit}
                    label={label}
                />
            ))}
            {displayedSecondaryAttributes.length > 0 && (
                <>
                    <ExpandMoreButton
                        isExpanded={showSecondaryAttributes}
                        onClick={(event) => {
                            event.preventDefault();
                            setShowSecondaryAttributes(!showSecondaryAttributes);
                        }}
                        sx={{ margin: -3, p: 3 }}
                        text={t('plus')}
                    />
                    {showSecondaryAttributes &&
                        displayedSecondaryAttributes.map(({ attribute, attributeType, label }) => (
                            <SecondaryAttribute
                                key={`secondary-attribute-${label}`}
                                attribute={attribute}
                                attributeType={attributeType}
                                hit={hit}
                                label={label}
                            />
                        ))}
                </>
            )}
        </>
    );
};

export default SecondaryAttributes;
